import React from "react"
import {useTranslation} from "react-i18next"

// Components
import Kbd from "../../../../../components/modern/Kbd"
import Icon from "../../../../../components/modern/Icon"

// Geometry
import {ECG_LAYOUT_KEYS, EcgLayoutKey, GAIN_OPTIONS, SPEED_OPTIONS} from "../ModernEcgGrid/geometry"

// Hooks
import {ECG_ZOOM_MAX, ECG_ZOOM_MIN} from "../useEcgZoom"
import useScrollHints from "./useScrollHints"

// Styles
import useStyles from "./styles"
const ITEM_SELECTOR = "[data-tbitem]";

/** "12x1" reads as "12 × 1" in the select. */
function layoutLabel(key: EcgLayoutKey): string {
    return key.replace("x", " × ")
}

/**
 * @param count
 */
function dotIndexes(count: number): number[] {
    const list: number[] = [];

    for (let index = 0; index < count; index++) {
        list.push(index)
    }

    return list
}

type ModernRecordToolbarProps = {
    panelOpen: boolean
    onTogglePanel(): void
    layout: EcgLayoutKey
    onLayoutChange(layout: EcgLayoutKey): void
    layoutDisabled?: boolean
    /** mm/s */
    speed: number
    onSpeedChange(speed: number): void
    /** mm/mV */
    gain: number
    onGainChange(gain: number): void
    /** Stage display scale — 1 is the figure at its own size. */
    zoom: number
    onZoomIn(): void
    onZoomOut(): void
    onZoomReset(): void
    caliperOn: boolean
    onToggleCalipers(): void
    /**
     * Annotation mode swaps the review stage for the beat-boundary surface.
     * Hidden unless the record actually carries labels to correct, so an
     * ordinary reviewer never meets a button that would show them an empty
     * screen.
     */
    showAnnotation: boolean
    annotationOn: boolean
    onToggleAnnotation(): void
    sidebar: boolean
    onToggleSidebar(): void
    /** Approve affordances are hidden entirely on the read-only preview route. */
    showApprove: boolean
    /**
     * A conclusion or parameter edit is unsaved or still in flight. Approving
     * now would race that write against the approval — this is the Modern
     * equivalent of Classic's `isConclusionEditing || isParameterEditing`.
     */
    approveDisabled: boolean
    /** Why approve is blocked, when it is something more specific than edits saving. */
    approveBlockedReason?: string
    onApprove(): void
    onApproveNext(): void
    /** HOLTER records hand off to the desktop app rather than being signed here. */
    showEdit: boolean
    onEdit(): void
    compact?: boolean
}

/**
 * Dense 42 px control strip under the app bar.
 *
 * Ported from the prototype's `pages/Record/record.css` `.toolbar` block, and
 * now backed by real controls end to end: LAYOUT / SPEED / GAIN drive
 * <ModernEcgGrid/>'s geometry, CALIPERS mounts <ModernCaliperLayer/> over the
 * trace, and SIDEBAR swaps the info strip for a left rail plus a disposition
 * dock. The prototype's fabricated "Acquired ✓ · AI read ✓ · …" stepper is left
 * out: nothing in production tracks those stages, so it would be decoration
 * that reads as workflow state.
 *
 * @param props
 */
export default function ModernRecordToolbar(props: ModernRecordToolbarProps) {
    const styles = useStyles();
    const [t] = useTranslation("common");
    const hints = useScrollHints(ITEM_SELECTOR);
    const {ref: setRow} = hints;
    const rowRef = React.useRef<HTMLDivElement | null>(null);
    const setRowRef = React.useCallback((node: HTMLDivElement | null) => {
        rowRef.current = node;
        setRow(node)
    }, [setRow]);
    const scrollByPage = React.useCallback((direction: -1 | 1) => {
        const node = rowRef.current;

        if (node === null) {
            return
        }

        node.scrollBy({left: direction * node.clientWidth * 0.6, behavior: "smooth"})
    }, []);
    const {
        panelOpen, onTogglePanel,
        layout, onLayoutChange, layoutDisabled,
        speed, onSpeedChange,
        gain, onGainChange,
        zoom, onZoomIn, onZoomOut, onZoomReset,
        caliperOn, onToggleCalipers,
        showAnnotation, annotationOn, onToggleAnnotation,
        sidebar, onToggleSidebar,
        showApprove, approveDisabled, approveBlockedReason, onApprove, onApproveNext,
        showEdit, onEdit,
        compact,
    } = props;

    const editButton = showEdit ? (
        <button
            type="button"
            data-tbitem=""
            className={`${styles.btn} ${styles.btn__edit}`}
            onClick={onEdit}
        >
            {t("records.edit")}
        </button>
    ) : null;

    const controls = (
        <React.Fragment>
            {!compact && (
                <React.Fragment>
                    <button
                        type="button"
                        data-tbitem=""
                        className={`${styles.toggle} ${panelOpen ? styles.toggle__active : ""}`}
                        aria-pressed={panelOpen}
                        onClick={onTogglePanel}
                        title={t("modern.panel")}
                    >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                            <path d="M2.5 4h11M2.5 8h11M2.5 12h11"/>
                        </svg>
                        {t("modern.panel")}
                        <Kbd>[</Kbd>
                    </button>

                    <div className={styles.vsep}/>
                </React.Fragment>
            )}

            {/*
                Layout, speed, gain, zoom and the calipers all drive the review
                stage, which annotation mode replaces. Leaving them visible
                would leave a row of controls that quietly do nothing.
            */}
            {!annotationOn && (
            <React.Fragment>
            <div className={styles.tgroup} data-tbitem="">
                <span className={styles.tlabel}>{t("modern.layout")}</span>
                <select
                    className={styles.tselect}
                    value={layout}
                    aria-label={t("modern.layout")}
                    disabled={layoutDisabled === true}
                    title={layoutDisabled === true ? t("modern.layout_single_lead") : undefined}
                    onChange={event => onLayoutChange(event.target.value as EcgLayoutKey)}
                >
                    {ECG_LAYOUT_KEYS.map(key => (
                        <option key={key} value={key}>{layoutLabel(key)}</option>
                    ))}
                </select>
            </div>

            <div className={styles.tgroup} data-tbitem="">
                <span className={styles.tlabel}>{t("modern.speed")}</span>
                <select
                    className={styles.tselect}
                    value={speed}
                    aria-label={t("modern.speed")}
                    onChange={event => onSpeedChange(Number(event.target.value))}
                >
                    {SPEED_OPTIONS.map(option => (
                        <option key={option} value={option}>{option} mm/s</option>
                    ))}
                </select>
            </div>

            <div className={styles.tgroup} data-tbitem="">
                <span className={styles.tlabel}>{t("modern.gain")}</span>
                <select
                    className={styles.tselect}
                    value={gain}
                    aria-label={t("modern.gain")}
                    onChange={event => onGainChange(Number(event.target.value))}
                >
                    {GAIN_OPTIONS.map(option => (
                        <option key={option} value={option}>{option} mm/mV</option>
                    ))}
                </select>
            </div>

            {/*
              * The wheel is the primary gesture — this group is here so the
              * feature is discoverable, and so there is a way back to 100 %
              * that does not involve wheeling until the readout says so.
              */}
            {!compact && (
            <React.Fragment>
            <div className={styles.vsep}/>

            <div className={styles.tgroup} data-tbitem="">
                <span className={styles.tlabel}>{t("modern.zoom")}</span>
                <div className={styles.zoom}>
                    <button
                        type="button"
                        className={styles.zoom_btn}
                        onClick={onZoomOut}
                        disabled={zoom <= ECG_ZOOM_MIN}
                        title={t("modern.zoom_out")}
                        aria-label={t("modern.zoom_out")}
                    >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <path d="M3.5 8h9"/>
                        </svg>
                    </button>
                    <button
                        type="button"
                        className={styles.zoom_value}
                        onClick={onZoomReset}
                        title={t("modern.zoom_reset")}
                    >
                        {Math.round(zoom * 100)}%
                    </button>
                    <button
                        type="button"
                        className={styles.zoom_btn}
                        onClick={onZoomIn}
                        disabled={zoom >= ECG_ZOOM_MAX}
                        title={t("modern.zoom_in")}
                        aria-label={t("modern.zoom_in")}
                    >
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <path d="M8 3.5v9M3.5 8h9"/>
                        </svg>
                    </button>
                </div>
            </div>
            </React.Fragment>
            )}

            <div className={styles.vsep}/>

            <button
                type="button"
                data-tbitem=""
                className={`${styles.toggle} ${caliperOn ? styles.toggle__active : ""}`}
                aria-pressed={caliperOn}
                onClick={onToggleCalipers}
                title={t("modern.calipers")}
            >
                <svg
                    viewBox="0 0 16 16"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                >
                    <path d="M3 2.5v11M13 2.5v11M5.5 8h5M7 6.5L5.5 8 7 9.5M9 6.5L10.5 8 9 9.5"/>
                </svg>
                {t("modern.calipers")}
                {!compact && <Kbd>C</Kbd>}
            </button>
            </React.Fragment>
            )}

            {showAnnotation && (
                <button
                    type="button"
                    data-tbitem=""
                    className={`${styles.toggle} ${annotationOn ? styles.toggle__active : ""}`}
                    aria-pressed={annotationOn}
                    onClick={onToggleAnnotation}
                    title={t("annotation.button")}
                >
                    <svg
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M1.5 10h2l1.5-5 2 8 2-6 1.5 3h4"/>
                        <path d="M5 1.5v13M11 1.5v13" strokeDasharray="2 2" opacity=".6"/>
                    </svg>
                    {annotationOn ? t("annotation.review_button") : t("annotation.button")}
                </button>
            )}

            {/* The sidebar arranges the review cards, none of which exist here. */}
            {!compact && !annotationOn && (
                <React.Fragment>
                    <div className={styles.vsep}/>

                    <button
                        type="button"
                        data-tbitem=""
                        className={`${styles.toggle} ${sidebar ? styles.toggle__active : ""}`}
                        aria-pressed={sidebar}
                        onClick={onToggleSidebar}
                        title={t("modern.sidebar")}
                    >
                        <svg
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <rect x="2.2" y="2.8" width="11.6" height="10.4" rx="1.6"/>
                            <path d="M6.4 2.8v10.4"/>
                        </svg>
                        {t("modern.sidebar")}
                        <Kbd>S</Kbd>
                    </button>
                </React.Fragment>
            )}

            <div className={styles.spacer}/>

            {compact && editButton}
        </React.Fragment>
    );

    const row = (
        <div
            className={`${styles.toolbar} ${compact ? styles.toolbar__compact : ""}`}
            ref={compact ? setRowRef : undefined}
        >
            {compact ? controls : <div className={styles.scroller}>{controls}</div>}
            {!compact && editButton}

            {showApprove && !compact && (
                <div className={styles.approve_group} data-tbitem="">
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btn__approve}`}
                        disabled={approveDisabled}
                        title={approveDisabled ? (approveBlockedReason || t("modern.approve_blocked")) : undefined}
                        onClick={onApprove}
                    >
                        <Icon name="check"/>
                        {t("approve")}
                    </button>
                    <button
                        type="button"
                        className={`${styles.btn} ${styles.btn__approve_next}`}
                        disabled={approveDisabled}
                        onClick={onApproveNext}
                        title={approveDisabled ? (approveBlockedReason || t("modern.approve_blocked")) : t("modern.approve_next")}
                    >
                        <Icon name="check"/>
                        {t("modern.approve_next")}
                    </button>
                </div>
            )}
        </div>
    );

    if (!compact) {
        return row
    }
    return (
        <div className={styles.mobilebar}>
            <div className={styles.frame}>
                {row}

                <button
                    type="button"
                    aria-hidden={!hints.moreLeft}
                    aria-label={t("modern.scroll_left")}
                    tabIndex={hints.moreLeft ? 0 : -1}
                    onClick={() => scrollByPage(-1)}
                    className={
                        `${styles.fade} ${styles.fade__left}` +
                        `${hints.moreLeft ? ` ${styles.fade__on} ${styles.fade__clickable}` : ""}` +
                        `${hints.hint ? ` ${styles.hint__left}` : ""}`
                    }
                >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M10 3.5L5.5 8l4.5 4.5"/>
                    </svg>
                </button>

                <button
                    type="button"
                    aria-hidden={!hints.moreRight}
                    aria-label={t("modern.scroll_right")}
                    tabIndex={hints.moreRight ? 0 : -1}
                    onClick={() => scrollByPage(1)}
                    className={
                        `${styles.fade} ${styles.fade__right}` +
                        `${hints.moreRight ? ` ${styles.fade__on} ${styles.fade__clickable}` : ""}` +
                        `${hints.hint ? ` ${styles.hint__right}` : ""}`
                    }
                >
                    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 3.5L10.5 8 6 12.5"/>
                    </svg>
                </button>
            </div>

            {hints.scrollable && (
                <div className={styles.dots} aria-hidden="true">
                    {dotIndexes(hints.dots).map(index => (
                        <span
                            key={index}
                            className={`${styles.dot} ${index === hints.active ? styles.dot__active : ""}`}
                        />
                    ))}
                </div>
            )}
        </div>
    )
}
